import { useEffect, useMemo, useState } from "react";
import PhoneInput from "@components/atoms/PhoneInput";
import { Modal, TextInput } from "@mantine/core";
import { useLocalStorage } from "@hooks/utils/localStorage";
import { StorageKeys } from "@components/Data/enums";
import { defaultCountry } from "@components/health-insurance-plan/summary/data";
import DatePickerCalculator from "@components/utils/DatePickerCalculator";
import { useRouter } from "next/router";
import { getAge, usePostKyc } from "@components/hooks";
import ConsentCheckBox from "@components/molecules/EthinosHomePage/LeftHeroComponent/ConsentCheckBox";

type IsdsCountry = {
  ISD: string;
  country: string;
  country_code: string;
  country_name: string;
  minimum: string;
  maximum: string;
}
const QuoteModel = ({ opened = false, setPopover, isds = [] }) => {
  const [success, setSuccess] = useState(false);
  const [generatingOTP, setGeneratingOTP] = useState(false);
  const [phoneError, setPhoneError] = useState<string>("");
  const [isButtonEnabled, setIsButtonEnabled] = useState(false);
  const [dobError, setDobError] = useState<string>("");
  const [getQuoteForm, setGetQuoteForm] = useLocalStorage(
    StorageKeys.GET_QUOTE_FORM,
    {
      country: defaultCountry,
      name: "",
      dob: "",
      phoneNumber: "",
    },
    true
  );
  const [dob, setDob] = useState<string | null | undefined>(
    getQuoteForm?.dob ?? null
  );
  const [isChecked, setIsChecked] = useState(true);
  const router = useRouter();

  const { trigger, isMutating } = usePostKyc({
    onSuccess: (data) => {
      if (data?.success) {
        if (typeof window !== "undefined" && (window as any).dataLayer) {
          (window as any).dataLayer.push({
            event: "Health Insurance Form - Customer DOB",
            click_text: "DOB Filled",
            dob: getQuoteForm.dob,
            age: getAge(getQuoteForm?.dob),
            currentPage: window.location.href,
            nextPage: `${window.location.host}/health-insurance-plan/summary`,
          });
          (window as any).dataLayer.push({
            event: "Virtual Page Path",
            vpv: "vpv/health-insurance/customer-dob",
          });
        }
        const qp = router.query;
        const productType =
          (qp.productType as string) ||
          getQuoteForm?.productType ||
          "reassurev3";
        const initData = getQuoteForm?.initData;
        const multi =
          qp.multi ||
          (initData &&
            ![
              "aspire-saver",
              "go-active",
              "health-premia",
              "health-companion",
              "health-recharge",
              "heartbeat",
              "health-pulse",
            ].includes(productType)
            ? "true"
            : "false");

        router.push({
          pathname: "/health-insurance-plan/summary",
          query: {
            multi,
            productType,
            retailFlag: true,
            isRelog: qp.isRelog,
            copay: qp.copay,
          },
        });
      }
    },
    onError: () => console.error("KYC API failed"),
  });

  let isdsCountry = isds || isds
  const countDigits = (val: string) => (val ? val.replace(/\D/g, "").length : 0);
  const selectedCountryObj: IsdsCountry | undefined = useMemo(() => {
    const countryVal = getQuoteForm?.country;
    if (!countryVal) return undefined;
    if (typeof countryVal === "object" && "ISD" in countryVal) return countryVal as IsdsCountry;

    return isdsCountry.find(
      (c: any) =>
        c.country_code?.toLowerCase() === String(countryVal)?.toLowerCase() ||
        c.ISD === countryVal ||
        c.country_name?.toLowerCase() === String(countryVal)?.toLowerCase()
    );
  }, [getQuoteForm?.country, isdsCountry]);

  const getAgeYears = (iso?: string | null) => {
    if (!iso) return 0;
    const b = new Date(iso);
    const now = new Date();
    let age = now.getFullYear() - b.getFullYear();
    const m = now.getMonth() - b.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < b.getDate())) age--;
    return age;
  };
  const onDobChange = (name?: string, value?: Date | null) => {
    const iso = value ? new Date(value).toISOString() : null;
    setDob(iso);
    setGetQuoteForm((prev: any) => ({ ...(prev || {}), dob: iso }));

    const age = getAgeYears(iso);
    if (iso && age < 18) {
      setDobError("Your date of birth should be atleast 18 years");
    } else {
      setDobError("");
    }
  };

  const onMobileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e?.target?.value ?? "";
    let digits = val.replace(/\D/g, "");
    const max = Number(selectedCountryObj?.maximum ?? "40");
    if (!isNaN(max) && digits.length > max) {
      digits = digits.slice(0, max);
      setPhoneError(`Maximum length is ${max} digits.You can not enter more than this`)
    }
    else {
      setPhoneError("")
    }
    setGetQuoteForm(prev => ({ ...(prev || {}), phoneNumber: digits }));
    if (success) setSuccess(false);
  }

  const onMobileReset = () => {
    setGetQuoteForm(prev => ({ ...(prev || {}), phoneNumber: "" }));
    setPhoneError("");
    setIsButtonEnabled(false);
  };

  const onCountryChange = (val: { country: string; ISD: string }) => {
    console.log("countryVlaue", val)
    setGetQuoteForm(prev => ({ ...(prev || {}), country: val, phoneNumber: "" }));
    setPhoneError("");
    setIsButtonEnabled(false)
  };

  const handleGetQuoteClick = async () => {
    setGeneratingOTP(true);
    try {
      const forms: any = {
        name: getQuoteForm?.name ?? "",
        dateOfBirth: getQuoteForm.dob,
        country: {
          isd: getQuoteForm?.country?.ISD,
          name: getQuoteForm?.country?.country
        }
      }
      if (getQuoteForm?.country?.ISD === "+91") forms.phoneNumber = getQuoteForm?.phoneNumber;
      else forms.intPhoneNumber = getQuoteForm?.phoneNumber;
      const payload = {
        ipAddress: undefined,
        campaignId: router.query?.cid ?? undefined,
        utmLeadFields: {
          utmSource: router?.query?.utm_source ?? undefined,
          utmCampaign: router.query?.utm_campaign ?? undefined,
          utmTerm: router.query?.utm_term ?? undefined,
          utmMedium: router.query?.utm_medium ?? undefined
        },
        channel: getQuoteForm.channel || "Retail",
        subChannel: getQuoteForm.subChannel || "Website",
        department: getQuoteForm.department || "SWebsite",
        agentName: getQuoteForm.agentName || "",
        language: "english"
      }
      await trigger({ data: { ...payload, forms } } as any)
    }
    catch (err) {
      console.log("KYC trigger error", err);
    }
    finally {
      setGeneratingOTP(false);
      setPopover(false)
    }
  }

  useEffect(() => {
    const name = (getQuoteForm?.name ?? "").toString().trim();
    const phoneNumber = (getQuoteForm?.phoneNumber ?? "")?.toString()?.replace(/\D/g, "")?.trim();
    const dobVal = (getQuoteForm?.dob ?? "").toString().trim();

    const validPhone = phoneNumber.length >= Number(selectedCountryObj?.minimum);
    const validName = name.length > 0;
    const age = getAgeYears(dobVal);
    const validDob = dobVal.length > 0 && age >= 18;
    setIsButtonEnabled(validName && validPhone && validDob);
  }, [getQuoteForm]);

  useEffect(() => {
    const phoneVal = String(getQuoteForm?.phoneNumber ?? "");
    const digits = countDigits(phoneVal);
    const min = Number(selectedCountryObj?.minimum ?? "0");
    const max = Number(selectedCountryObj?.maximum ?? "40");
    if (digits === 0) {
      setPhoneError("");
      setIsButtonEnabled(false);
      return;
    }
    if (!isNaN(min) && digits < min) {
      setPhoneError(`Please enter at least ${min} digits mobile number`);
      setIsButtonEnabled(false);
      return;
    }
    if (!isNaN(max) && digits > max) {
      setPhoneError(`Please do not enter more than ${max} digits mobile number`);
      setIsButtonEnabled(false);
      return;
    }
    setPhoneError("");
  }, [getQuoteForm?.phoneNumber, selectedCountryObj]);

  return (
    <>
      <Modal.Root opened={opened} centered onClose={() => setPopover(false)}
        classNames={{
          content: 'border-2 border-nivabupa-ethinosHomeOrange rounded-xl overflow-hidden',
          header: 'p-0',
        }}>
        <Modal.Overlay />
        <Modal.Content className="overflow-hidden">
          <Modal.Body>
            <Modal.CloseButton className="text-nivabupa-blue-ethinosPlanRecommendation absolute right-1 top-2" />
            <h3 className="text-nivabupa-blue-ethinosPlanRecommendation font-bold md:text-xl text-center text-base py-4">Please share the following details</h3>
            <form>
              <div className="pb-5 bg-white rounded-lg  relative lg:w-full">
                <div className="flex flex-col w-full mb-2">
                  <label className="text-sm text-nivabupa-ethinosBlack font-semibold mb-2 block"
                  >Mobile number*</label>
                  <div className="border border-[#ced4da] w-full rounded-[3px] flex flex-col">
                    <PhoneInput
                      success={success}
                      isLoading={generatingOTP}
                      isds={isdsCountry as unknown as Record<string, string>[]}
                      inputValue={getQuoteForm?.phoneNumber ?? ""}
                      countryValue={getQuoteForm?.country || defaultCountry}
                      onMobileChange={onMobileChange}
                      onCountryChange={onCountryChange}
                      isMask={false}
                      onMobileReset={onMobileReset}
                      newDesignConfig={false}
                      id={"cta-topbar-mobile-number"}
                    />
                  </div>
                  {phoneError && (
                    <div className="text-red-500 text-[12px] mt-1">
                      {phoneError}
                    </div>
                  )}
                </div>
                <div className="flex flex-col w-full mb-2">
                   <label className="text-sm text-nivabupa-ethinosBlack font-semibold mb-2 block"
                  >Name</label>
                  <TextInput                    
                    placeholder="Enter your full name"
                    id="cta-quote-name"
                    size="sm"
                    required
                    radius="sm"
                    withAsterisk
                    value={getQuoteForm?.name ?? ""}
                    onChange={(e) => {
                      setGetQuoteForm((prev: any) => ({
                        ...(prev || {}),
                        name: e.target.value,
                      }));
                    }}
                    className="w-full"
                    styles={{
                      input: {
                        height: "45px",
                      },
                    }}
                  />
                </div>
                <div className=" md:flex-row flex-col md:gap-10 gap-3 w-full mb-2">
                   <label className="text-sm text-nivabupa-ethinosBlac font-semibold mb-2 block"
                  >Date of birth</label>
                  <DatePickerCalculator
                    value={getQuoteForm.dob ? new Date(getQuoteForm.dob) : null}
                    name="dob"                   
                    onChange={(name, value) => onDobChange(name, value)}
                    error={undefined}
                  />
                  {dobError && <div className="text-red-500 text-[12px] mt-1">{dobError}</div>}
                </div>
                <ConsentCheckBox
                  activeStep={1}
                  isCheckedTerms={isChecked}
                  onClickCheckBox={(val: boolean) => setIsChecked(val)}
                />
                 <div id="get-quote-summary" className="flex md:flex-row flex-col md:gap-10 gap-3 w-full">
                    <button id="get-quote-summary" type="button" onClick={handleGetQuoteClick}
                      disabled={!isButtonEnabled}
                      className={`flex flex-row items-center mt-1 gap-1.5 w-full px-3 text-center rounded-lg  text-white disabled:opacity-50 md:h-[54px] h-[39px] 
                                ${isButtonEnabled ? " bg-nivabupa-ethinosHomeOrange cursor-pointer" : "bg-gray-400 cursor not allowed"} `} >
                      <div className="w-full flex flex-row justify-center items-center">
                        <span id="get-quote-summary" className="text-sm font-bold uppercase justify-center w-full md:text-base md:flex">
                          Get Quote
                        </span>
                      </div>
                    </button>
                  </div>
              </div>
            </form>
          </Modal.Body>
        </Modal.Content>
      </Modal.Root>
    </>
  );
};

export default QuoteModel;
